import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';

type Preference = 'partial' | 'full';

type DeliveryState = {
    delivery_preference: Preference | null;
    label: string | null;
    updated_by: string | null;
    updated_at: string | null;
    can_edit: boolean;
};

/** Escolha do cliente quando parte sai do estoque e parte é importada (reunião Supply Chain). */
export default function DealDeliveryPreference({ dealId }: { dealId: number }) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const queryKey = ['ploomes-deal-delivery', dealId];

    const { data } = useQuery({
        queryKey,
        queryFn: async () => (await api.get<{ data: DeliveryState }>(`/ploomes-deals/${dealId}/delivery-preference`)).data.data,
    });

    const save = useMutation({
        mutationFn: async (value: Preference | null) =>
            (await api.put<{ data: DeliveryState; message: string }>(`/ploomes-deals/${dealId}/delivery-preference`, {
                delivery_preference: value,
            })).data,
        onSuccess: (res) => {
            qc.setQueryData(queryKey, res.data);
            toast.success(res.message);
        },
        onError: () => toast.error(t('deal.delivery_error')),
    });

    if (!data) return null;

    const options: { value: Preference; label: string }[] = [
        { value: 'partial', label: t('deal.delivery_partial') },
        { value: 'full', label: t('deal.delivery_full') },
    ];

    return (
        <div className="flex flex-wrap items-center gap-2" title={t('deal.delivery_hint')}>
            <span className="font-medium text-gray-700 dark:text-zinc-200">{t('deal.delivery_preference')}:</span>
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800" role="radiogroup">
                {options.map((opt) => {
                    const active = data.delivery_preference === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            disabled={!data.can_edit || save.isPending}
                            onClick={() => save.mutate(active ? null : opt.value)}
                            className={cn(
                                'rounded-md px-2.5 py-1 transition-colors disabled:cursor-not-allowed',
                                active
                                    ? 'bg-white font-semibold text-gray-900 shadow-sm ring-1 ring-gray-200 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-600'
                                    : 'text-gray-600 hover:text-gray-900 disabled:opacity-60 dark:text-zinc-400',
                            )}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
            {!data.delivery_preference && (
                <span className="text-amber-600 dark:text-amber-400">{t('deal.delivery_unset')}</span>
            )}
            {data.updated_by && data.delivery_preference && (
                <span className="text-gray-400">· {data.updated_by}</span>
            )}
        </div>
    );
}
